import React, { useState, useEffect, useRef } from "react";
import {
	ChevronLeft,
	ChevronRight,
	ChevronDown,
	Calendar,
	Filter,
} from "lucide-react";
import { CircularProgress, Typography } from "@mui/material";

// Custom CSS for PTSachNr Sidebar scrollbar - Always visible when needed
const ptsachnrSidebarScrollbarStyles = `
  .ptsachnr-sidebar-content::-webkit-scrollbar {
    width: 14px;
    background: #f8f8f8;
  }
  .ptsachnr-sidebar-content::-webkit-scrollbar-track {
    background: #f8f8f8;
    border-radius: 8px;
    border: 1px solid #d8d8d8;
  }
  .ptsachnr-sidebar-content::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 8px;
    border: 2px solid #f8f8f8;
    min-height: 40px;
    transition: all 0.2s ease;
  }
  .ptsachnr-sidebar-content::-webkit-scrollbar-thumb:hover {
    background: grey;
    border: 1px solid #f8f8f8;
    cursor: pointer;
  }
  .ptsachnr-sidebar-content::-webkit-scrollbar-thumb:active {
    background: grey;
  }
  .ptsachnr-sidebar-content::-webkit-scrollbar-corner {
    background: #f8f8f8;
  }
  
  /* Enhanced scrollbar visibility for Firefox */
  .ptsachnr-sidebar-content {
    scrollbar-width: auto !important;
    scrollbar-color: grey #f8f8f8 !important;
  }
  
  /* Add scroll indicators */
  .ptsachnr-sidebar-scroll-indicator-top,
  .ptsachnr-sidebar-scroll-indicator-bottom {
    position: sticky;
    left: 0;
    right: 0;
    height: 8px;
    pointer-events: none;
    z-index: 10;
    transition: opacity 0.3s ease;
  }
  
  .ptsachnr-sidebar-scroll-indicator-top {
    top: 0;
  }
  
  .ptsachnr-sidebar-scroll-indicator-bottom {
    bottom: 0;
  }
`;

// Part number prefixes for filtering (same as VFF sidebar)
const PART_NO_PREFIXES = [
	"P",
	"V",
	"92.",
	"81.",
	"06",
	"04",
	"03",
	"01",
	"07",
	"51.",
	"K",
	"M",
	"82",
	"85",
	"U1.",
];

// Inject the scrollbar styles into the document head
if (typeof document !== "undefined") {
	const existingStyle = document.getElementById(
		"ptsachnr-sidebar-scrollbar-styles",
	);
	if (!existingStyle) {
		const style = document.createElement("style");
		style.id = "ptsachnr-sidebar-scrollbar-styles";
		style.textContent = ptsachnrSidebarScrollbarStyles;
		document.head.appendChild(style);
	}
}

const PTSachNrSidebar = ({
	onFiltersChange,
	onClearFilters,
	onColumnVisibilityChange,
	columnVisibility = {},
	filterOptions = {},
	selectedViewType = "Prototype",
	onViewTypeChange = () => {},
	isLoading = false,
	error = null,
}) => {
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [showColumnSelector, setShowColumnSelector] = useState(false);
	const [isAccordionOpen, setIsAccordionOpen] = useState(false);
	const [hasScrollableContent, setHasScrollableContent] = useState(false);
	const [canScrollUp, setCanScrollUp] = useState(false);
	const [canScrollDown, setCanScrollDown] = useState(false);
	const contentRef = useRef(null);
	const [openDropdowns, setOpenDropdowns] = useState({
		programm: false,
		project: false,
		purchaseType: false,
		purchaseTypeSystem: false,
		seTeams: false,
		rampUpGroup: false,
		flag: false,
		aenderungsnummer: false,
		changeDescription: false,
		dept: false,
		partNoPrefix: false,
		columns: false,
	});
	const {
		programms = [],
		projects = [],
		purchaseTypes = [],
		purchaseTypeSystems = [],
		seTeams = [],
		rampUpGroups = [],
		flags = [],
		aenderungsnummers = [],
		changeDescriptions = [],
		fets = [],
		depts = [], // dynamic-filtering mappings from backend
		projectPurchaseTypeMapping = {},
		projectSeTeamsMapping = {},
		projectRampUpGroupMapping = {},
		projectAenderungsnummerMapping = {},
		projectChangeDescriptionMapping = {},
		projectDeptMapping = {},
		programmPurchaseTypeMapping = {},
		programmSeTeamsMapping = {},
		programmProjectMapping = {},
		programmRampUpGroupMapping = {},
		programmAenderungsnummerMapping = {},
		programmChangeDescriptionMapping = {},
		programmDeptMapping = {},
	} = filterOptions || {};
	const [selectedFilters, setSelectedFilters] = useState({
		project: [],
		programm: [],
		purchaseType: [],
		purchaseTypeSystem: [],
		seTeams: [],
		rampUpGroup: [],
		flag: [],
		aenderungsnummer: [],
		changeDescription: [],
		fet: { from: "", to: "" },
		dept: [],
		partNoPrefix: [],
		showMismatchOnly: false,
	}); // ── Dynamic filtering helpers ─────────────────────────────────────────────
	// Collect every unique value that appears in ANY of the given mapping entries.
	const mergeFromMapping = (mapping, selectedKeys) => {
		if (!selectedKeys || selectedKeys.length === 0) return null; // null → "no restriction"
		const merged = new Set();
		selectedKeys.forEach((key) => {
			(mapping[key] || []).forEach((v) => merged.add(v));
		});
		return Array.from(merged).sort();
	};

	// Intersect two arrays (or return the base array when there is no restriction).
	const intersect = (base, restriction) => {
		if (restriction === null) return base;
		const set = new Set(restriction);
		return base.filter((v) => set.has(v));
	};

	// ── Auto-clear stale selections when parent filters change ───────────────
	// When the user changes programm selection, drop any project / purchaseType /
	// seTeams values that are no longer in the restricted set so we never send
	// contradictory filters to the backend.
	useEffect(() => {
		const selectedProgramms = selectedFilters.programm || [];
		if (selectedProgramms.length === 0) return;

		setSelectedFilters((prev) => {
			const allowedProjects =
				mergeFromMapping(programmProjectMapping, selectedProgramms) || projects;
			const allowedPurchaseTypes =
				mergeFromMapping(programmPurchaseTypeMapping, selectedProgramms) ||
				purchaseTypes;
			const allowedSeTeams =
				mergeFromMapping(programmSeTeamsMapping, selectedProgramms) || seTeams;
			const allowedRampUpGroups =
				mergeFromMapping(programmRampUpGroupMapping, selectedProgramms) ||
				rampUpGroups;
			const allowedAenderungsnummers =
				mergeFromMapping(programmAenderungsnummerMapping, selectedProgramms) ||
				aenderungsnummers;
			const allowedChangeDescriptions =
				mergeFromMapping(programmChangeDescriptionMapping, selectedProgramms) ||
				changeDescriptions;
			const allowedDepts =
				mergeFromMapping(programmDeptMapping, selectedProgramms) || depts;

			const nextProject = (prev.project || []).filter((v) =>
				new Set(allowedProjects).has(v),
			);
			const nextPurchaseType = (prev.purchaseType || []).filter((v) =>
				new Set(allowedPurchaseTypes).has(v),
			);
			const nextSeTeams = (prev.seTeams || []).filter((v) =>
				new Set(allowedSeTeams).has(v),
			);
			const nextRampUpGroup = (prev.rampUpGroup || []).filter((v) =>
				new Set(allowedRampUpGroups).has(v),
			);
			const nextAenderungsnummer = (prev.aenderungsnummer || []).filter((v) =>
				new Set(allowedAenderungsnummers).has(v),
			);
			const nextChangeDescription = (prev.changeDescription || []).filter((v) =>
				new Set(allowedChangeDescriptions).has(v),
			);
			const nextDept = (prev.dept || []).filter((v) =>
				new Set(allowedDepts).has(v),
			);

			if (
				nextProject.length === (prev.project || []).length &&
				nextPurchaseType.length === (prev.purchaseType || []).length &&
				nextSeTeams.length === (prev.seTeams || []).length &&
				nextRampUpGroup.length === (prev.rampUpGroup || []).length &&
				nextAenderungsnummer.length === (prev.aenderungsnummer || []).length &&
				nextChangeDescription.length ===
					(prev.changeDescription || []).length &&
				nextDept.length === (prev.dept || []).length
			)
				return prev;

			return {
				...prev,
				project: nextProject,
				purchaseType: nextPurchaseType,
				seTeams: nextSeTeams,
				rampUpGroup: nextRampUpGroup,
				aenderungsnummer: nextAenderungsnummer,
				changeDescription: nextChangeDescription,
				dept: nextDept,
			};
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFilters.programm]);

	// When the user changes project selection, drop dependent values that are no longer valid.
	useEffect(() => {
		const selectedProjects = selectedFilters.project || [];
		if (selectedProjects.length === 0) return;

		setSelectedFilters((prev) => {
			const allowedPurchaseTypes =
				mergeFromMapping(projectPurchaseTypeMapping, selectedProjects) ||
				purchaseTypes;
			const allowedSeTeams =
				mergeFromMapping(projectSeTeamsMapping, selectedProjects) || seTeams;
			const allowedRampUpGroups =
				mergeFromMapping(projectRampUpGroupMapping, selectedProjects) ||
				rampUpGroups;
			const allowedAenderungsnummers =
				mergeFromMapping(projectAenderungsnummerMapping, selectedProjects) ||
				aenderungsnummers;
			const allowedChangeDescriptions =
				mergeFromMapping(projectChangeDescriptionMapping, selectedProjects) ||
				changeDescriptions;
			const allowedDepts =
				mergeFromMapping(projectDeptMapping, selectedProjects) || depts;

			const nextPurchaseType = (prev.purchaseType || []).filter((v) =>
				new Set(allowedPurchaseTypes).has(v),
			);
			const nextSeTeams = (prev.seTeams || []).filter((v) =>
				new Set(allowedSeTeams).has(v),
			);
			const nextRampUpGroup = (prev.rampUpGroup || []).filter((v) =>
				new Set(allowedRampUpGroups).has(v),
			);
			const nextAenderungsnummer = (prev.aenderungsnummer || []).filter((v) =>
				new Set(allowedAenderungsnummers).has(v),
			);
			const nextChangeDescription = (prev.changeDescription || []).filter((v) =>
				new Set(allowedChangeDescriptions).has(v),
			);
			const nextDept = (prev.dept || []).filter((v) =>
				new Set(allowedDepts).has(v),
			);

			if (
				nextPurchaseType.length === (prev.purchaseType || []).length &&
				nextSeTeams.length === (prev.seTeams || []).length &&
				nextRampUpGroup.length === (prev.rampUpGroup || []).length &&
				nextAenderungsnummer.length === (prev.aenderungsnummer || []).length &&
				nextChangeDescription.length ===
					(prev.changeDescription || []).length &&
				nextDept.length === (prev.dept || []).length
			)
				return prev;

			return {
				...prev,
				purchaseType: nextPurchaseType,
				seTeams: nextSeTeams,
				rampUpGroup: nextRampUpGroup,
				aenderungsnummer: nextAenderungsnummer,
				changeDescription: nextChangeDescription,
				dept: nextDept,
			};
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFilters.project]);

	const getFilteredOptions = (filterType) => {
		const selectedProgramms = selectedFilters.programm || [];
		const selectedProjects = selectedFilters.project || [];
		const hasProgram = selectedProgramms.length > 0;
		const hasProject = selectedProjects.length > 0;

		switch (filterType) {
			case "programms":
				return programms;

			case "projects": {
				// Restrict by selected programms if any
				const byProgramm = mergeFromMapping(
					programmProjectMapping,
					selectedProgramms,
				);
				return intersect(projects, byProgramm);
			}

			case "purchaseTypes": {
				// Union restrictions from selected programms and selected projects
				const byProgramm = mergeFromMapping(
					programmPurchaseTypeMapping,
					selectedProgramms,
				);
				const byProject = mergeFromMapping(
					projectPurchaseTypeMapping,
					selectedProjects,
				);
				// If both are set, use intersection; if only one is set, use that one
				if (hasProgram && hasProject) {
					const pSet = new Set(byProgramm || purchaseTypes);
					const prSet = new Set(byProject || purchaseTypes);
					return purchaseTypes.filter((v) => pSet.has(v) && prSet.has(v));
				}
				if (hasProgram) return intersect(purchaseTypes, byProgramm);
				if (hasProject) return intersect(purchaseTypes, byProject);
				return purchaseTypes;
			}

			case "seTeams": {
				const byProgramm = mergeFromMapping(
					programmSeTeamsMapping,
					selectedProgramms,
				);
				const byProject = mergeFromMapping(
					projectSeTeamsMapping,
					selectedProjects,
				);
				if (hasProgram && hasProject) {
					const pSet = new Set(byProgramm || seTeams);
					const prSet = new Set(byProject || seTeams);
					return seTeams.filter((v) => pSet.has(v) && prSet.has(v));
				}
				if (hasProgram) return intersect(seTeams, byProgramm);
				if (hasProject) return intersect(seTeams, byProject);
				return seTeams;
			}
			case "rampUpGroups": {
				const byProgramm = mergeFromMapping(
					programmRampUpGroupMapping,
					selectedProgramms,
				);
				const byProject = mergeFromMapping(
					projectRampUpGroupMapping,
					selectedProjects,
				);
				if (hasProgram && hasProject) {
					const pSet = new Set(byProgramm || rampUpGroups);
					const prSet = new Set(byProject || rampUpGroups);
					return rampUpGroups.filter((v) => pSet.has(v) && prSet.has(v));
				}
				if (hasProgram) return intersect(rampUpGroups, byProgramm);
				if (hasProject) return intersect(rampUpGroups, byProject);
				return rampUpGroups;
			}

			case "aenderungsnummers": {
				const byProgramm = mergeFromMapping(
					programmAenderungsnummerMapping,
					selectedProgramms,
				);
				const byProject = mergeFromMapping(
					projectAenderungsnummerMapping,
					selectedProjects,
				);
				if (hasProgram && hasProject) {
					const pSet = new Set(byProgramm || aenderungsnummers);
					const prSet = new Set(byProject || aenderungsnummers);
					return aenderungsnummers.filter((v) => pSet.has(v) && prSet.has(v));
				}
				if (hasProgram) return intersect(aenderungsnummers, byProgramm);
				if (hasProject) return intersect(aenderungsnummers, byProject);
				return aenderungsnummers;
			}

			case "changeDescriptions": {
				const byProgramm = mergeFromMapping(
					programmChangeDescriptionMapping,
					selectedProgramms,
				);
				const byProject = mergeFromMapping(
					projectChangeDescriptionMapping,
					selectedProjects,
				);
				if (hasProgram && hasProject) {
					const pSet = new Set(byProgramm || changeDescriptions);
					const prSet = new Set(byProject || changeDescriptions);
					return changeDescriptions.filter((v) => pSet.has(v) && prSet.has(v));
				}
				if (hasProgram) return intersect(changeDescriptions, byProgramm);
				if (hasProject) return intersect(changeDescriptions, byProject);
				return changeDescriptions;
			}

			case "depts": {
				const byProgramm = mergeFromMapping(
					programmDeptMapping,
					selectedProgramms,
				);
				const byProject = mergeFromMapping(
					projectDeptMapping,
					selectedProjects,
				);
				if (hasProgram && hasProject) {
					const pSet = new Set(byProgramm || depts);
					const prSet = new Set(byProject || depts);
					return depts.filter((v) => pSet.has(v) && prSet.has(v));
				}
				if (hasProgram) return intersect(depts, byProgramm);
				if (hasProject) return intersect(depts, byProject);
				return depts;
			}
			case "flags":
				return flags;
			case "fets":
				return fets;
			case "purchaseTypeSystems":
				return purchaseTypeSystems;
			case "partNoPrefix":
				return PART_NO_PREFIXES;
			default:
				return [];
		}
	};

	const [localColumnVisibility, setLocalColumnVisibility] = useState({
		rampUpGroup: true,
		seTeams: true,
		purchaseType: true,
		supplier: true,
		...columnVisibility,
	});

	const [columnsToHide, setColumnsToHide] = useState([]);

	const handleColumnVisibilityChange = (column, isVisible) => {
		const newVisibility = {
			...localColumnVisibility,
			[column]: isVisible,
		};
		setLocalColumnVisibility(newVisibility);
		if (onColumnVisibilityChange) {
			onColumnVisibilityChange(newVisibility);
		}
	};

	const toggleColumnHide = (column) => {
		setColumnsToHide((prev) => {
			const isCurrentlySelected = prev.includes(column);
			let newColumnsToHide;

			if (isCurrentlySelected) {
				newColumnsToHide = prev.filter((col) => col !== column);
			} else {
				newColumnsToHide = [...prev, column];
			}

			const newVisibility = {
				...localColumnVisibility,
				[column]: !newColumnsToHide.includes(column),
			};

			setLocalColumnVisibility(newVisibility);
			if (onColumnVisibilityChange) {
				onColumnVisibilityChange(newVisibility);
			}

			return newColumnsToHide;
		});
	};

	const toggleDropdown = (dropdownName) => {
		setOpenDropdowns((prev) => {
			const isCurrentlyOpen = prev[dropdownName];
			const closedDropdowns = {
				programm: false,
				project: false,
				purchaseType: false,
				seTeams: false,
				rampUpGroup: false,
				flag: false,
				aenderungsnummer: false,
				changeDescription: false,
				dept: false,
				partNoPrefix: false,
				columns: false,
				viewType: false,
			};

			return {
				...closedDropdowns,
				[dropdownName]: !isCurrentlyOpen,
			};
		});
	};

	const toggleAccordion = () => {
		setIsAccordionOpen(!isAccordionOpen);
	};

	const handleFilterToggle = (filterName, value) => {
		setSelectedFilters((prev) => {
			const currentValues = prev[filterName] || [];
			const isSelected = currentValues.includes(value);
			return {
				...prev,
				[filterName]: isSelected
					? currentValues.filter((v) => v !== value)
					: [...currentValues, value],
			};
		});
	};

	const handleHideColumnCheckboxChange = (column) => {
		toggleColumnHide(column);
	};

	const getFilterDropdownText = (filterName, options) => {
		const selected = selectedFilters[filterName] || [];
		if (selected.length === 0) {
			const labels = {
				project: "Select Project",
				programm: "Select Project Name",
				purchaseType: "Select Purchase Type",
				purchaseTypeSystem: "Select Purchase Type",
				seTeams: "Select SE-Team",
				rampUpGroup: "Select ALG",
				flag: "Select Flag",
				aenderungsnummer: "Select Change No",
				changeDescription: "Select Change Description",
				dept: "Select Dept",
				partNoPrefix: "Select Part No Prefix",
			};
			return labels[filterName];
		}
		if (selected.length === 1) return selected[0];
		return `${selected.length} items selected`;
	};

	const getHideColumnsDropdownText = () => {
		if (columnsToHide.length === 0) return "Select columns to hide";

		const hiddenColumnLabels = columnsToHide.map((key) => {
			const labels = {
				rampUpGroup: "Ramp-up group",
				seTeams: "SE-Teams",
				purchaseType: "Purchase Type",
				supplier: "Supplier",
			};
			return labels[key];
		});

		if (hiddenColumnLabels.length === 1)
			return `Hide: ${hiddenColumnLabels[0]}`;
		return `Hide: ${hiddenColumnLabels.length} columns`;
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest(".custom-dropdown")) {
				setOpenDropdowns({
					programm: false,
					project: false,
					purchaseType: false,
					seTeams: false,
					rampUpGroup: false,
					flag: false,
					aenderungsnummer: false,
					changeDescription: false,
					dept: false,
					partNoPrefix: false,
					columns: false,
				});
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const checkScrollable = () => {
			if (contentRef.current) {
				const hasScroll =
					contentRef.current.scrollHeight > contentRef.current.clientHeight;
				setHasScrollableContent(hasScroll);

				const scrollTop = contentRef.current.scrollTop;
				const scrollHeight = contentRef.current.scrollHeight;
				const clientHeight = contentRef.current.clientHeight;

				setCanScrollUp(scrollTop > 5);
				setCanScrollDown(scrollTop < scrollHeight - clientHeight - 5);
			}
		};

		const handleScroll = () => {
			if (contentRef.current) {
				const scrollTop = contentRef.current.scrollTop;
				const scrollHeight = contentRef.current.scrollHeight;
				const clientHeight = contentRef.current.clientHeight;

				setCanScrollUp(scrollTop > 5);
				setCanScrollDown(scrollTop < scrollHeight - clientHeight - 5);
			}
		};

		checkScrollable();

		if (contentRef.current) {
			contentRef.current.addEventListener("scroll", handleScroll);
		}

		const resizeObserver = new ResizeObserver(checkScrollable);
		if (contentRef.current) {
			resizeObserver.observe(contentRef.current);
		}

		window.addEventListener("resize", checkScrollable);

		return () => {
			if (contentRef.current) {
				contentRef.current.removeEventListener("scroll", handleScroll);
			}
			resizeObserver.disconnect();
			window.removeEventListener("resize", checkScrollable);
		};
	}, [isLoading, error, selectedFilters, openDropdowns]);

	useEffect(() => {
		if (!isLoading && contentRef.current) {
			const timer = setTimeout(() => {
				const hasScroll =
					contentRef.current.scrollHeight > contentRef.current.clientHeight;
				setHasScrollableContent(hasScroll);
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [isLoading, filterOptions]);

	const retryLoadingFilters = () => {
		window.location.reload();
	};
	const clearFilters = () => {
		const emptyFilters = {
			project: [],
			programm: [],
			purchaseType: [],
			purchaseTypeSystem: [],
			seTeams: [],
			rampUpGroup: [],
			flag: [],
			aenderungsnummer: [],
			changeDescription: [],
			fet: { from: "", to: "" },
			dept: [],
			partNoPrefix: [],
			showMismatchOnly: false,
		};
		setSelectedFilters(emptyFilters);
		onClearFilters();
	};

	const applyFilters = () => {
		const filtersToApply = {};
		Object.entries(selectedFilters).forEach(([key, value]) => {
			if (key === "fet") {
				filtersToApply[key] = value || { from: "", to: "" };
			} else if (key === "showMismatchOnly") {
				filtersToApply[key] = !!value;
			} else if (Array.isArray(value)) {
				filtersToApply[key] = value;
			} else if (typeof value === "string") {
				filtersToApply[key] = [value];
			} else {
				filtersToApply[key] = [];
			}
		});

		setIsSidebarCollapsed(true);
		setIsAccordionOpen(false);

		onFiltersChange(filtersToApply, selectedViewType);
	};

	const [searchTerms, setSearchTerms] = useState({});
	const [debouncedSearchTerms, setDebouncedSearchTerms] = useState({});

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearchTerms(searchTerms);
		}, 250);
		return () => clearTimeout(handler);
	}, [searchTerms]);

	const handleSearchChange = (filterName, value) => {
		setSearchTerms((prev) => ({ ...prev, [filterName]: value }));
	};

	const handleSelectAll = (filterName, filteredOptions) => {
		setSelectedFilters((prev) => {
			const current = prev[filterName] || [];
			const allSelected = filteredOptions.every((o) => current.includes(o));
			if (allSelected) {
				return {
					...prev,
					[filterName]: current.filter((v) => !filteredOptions.includes(v)),
				};
			} else {
				const merged = Array.from(new Set([...current, ...filteredOptions]));
				return { ...prev, [filterName]: merged };
			}
		});
	};
	const renderCustomDropdown = (
		filterName,
		options,
		label,
		searchMode = "contains",
	) => {
		const safeOptions = Array.isArray(options) ? options : [];
		const searchValue = debouncedSearchTerms[filterName] || "";
		const filteredOptions = searchValue
			? safeOptions.filter((option) =>
					searchMode === "startsWith"
						? String(option).toLowerCase().startsWith(searchValue.toLowerCase())
						: String(option).toLowerCase().includes(searchValue.toLowerCase()),
				)
			: safeOptions;

		// ── "filtered by …" badge logic ──────────────────────────────────────────
		const hasProgramm = (selectedFilters.programm || []).length > 0;
		const hasProject = (selectedFilters.project || []).length > 0; // Which parent filters restrict this dropdown?
		const DYNAMIC_FIELDS = [
			"project",
			"purchaseType",
			"seTeams",
			"rampUpGroup",
			"aenderungsnummer",
			"changeDescription",
			"dept",
		];
		const PROJECT_SCOPED_FIELDS = [
			"purchaseType",
			"seTeams",
			"rampUpGroup",
			"aenderungsnummer",
			"changeDescription",
			"dept",
		];

		const restrictedByProgramm =
			hasProgramm && DYNAMIC_FIELDS.includes(filterName);
		const restrictedByProject =
			hasProject && PROJECT_SCOPED_FIELDS.includes(filterName);

		// Build badge text
		const filterBadgeParts = [];
		if (restrictedByProgramm) filterBadgeParts.push("Project Name");
		if (restrictedByProject) filterBadgeParts.push("Project");
		const filterBadge =
			filterBadgeParts.length > 0
				? `filtered by ${filterBadgeParts.join(" & ")}`
				: null;

		// Total (unfiltered) count for footer — fall back to the base array from filterOptions
		const baseCountMap = {
			project: projects,
			purchaseType: purchaseTypes,
			purchaseTypeSystem: purchaseTypeSystems,
			seTeams: seTeams,
			rampUpGroup: rampUpGroups,
			aenderungsnummer: aenderungsnummers,
			changeDescription: changeDescriptions,
			dept: depts,
		};
		const totalOptions = baseCountMap[filterName] || safeOptions;
		// ─────────────────────────────────────────────────────────────────────────

		return (
			<div style={styles.filterGroup}>
				{" "}
				<label
					style={{
						...styles.filterLabel,
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						gap: "4px",
					}}
				>
					{label}
					{filterBadge && (
						<Filter
							style={{
								width: "12px",
								height: "12px",
								color: "#cc0000",
								marginLeft: "4px",
								flexShrink: 0,
							}}
							title={filterBadge}
						/>
					)}
				</label>
				<div style={styles.customDropdown} className="custom-dropdown">
					<div
						style={styles.dropdownButton}
						onClick={() => toggleDropdown(filterName)}
					>
						<span
							style={{
								color:
									(selectedFilters[filterName] || []).length === 0
										? "#999"
										: "#333",
								display: "inline-block",
								width: "calc(100% - 20px)",
							}}
						>
							{getFilterDropdownText(filterName, safeOptions)}
						</span>
						<span style={{ float: "right", marginTop: "2px" }}>
							<ChevronDown
								style={{ width: "16px", height: "16px", color: "#666" }}
							/>
						</span>
					</div>
					{openDropdowns[filterName] && (
						<div style={styles.dropdownContent}>
							<input
								type="text"
								placeholder={`Search ${label}`}
								value={searchTerms[filterName] || ""}
								onChange={(e) => handleSearchChange(filterName, e.target.value)}
								style={{
									width: "90%",
									margin: "8px 5%",
									padding: "6px",
									fontSize: "13px",
									border: "1px solid #ddd",
									borderRadius: "4px",
									fontFamily: "MAN EUROPE_Regular",
									boxSizing: "border-box",
									outline: "none",
									marginBottom: "4px",
								}}
							/>
							{filteredOptions.length > 0 && (
								<div
									style={{
										padding: "4px 12px 6px",
										borderBottom: "1px solid #eee",
										display: "flex",
										alignItems: "center",
										cursor: "pointer",
										fontSize: "12px",
										fontFamily: "MAN EUROPE_Bold",
										color: "#cc0000",
										gap: "6px",
									}}
									onClick={() => handleSelectAll(filterName, filteredOptions)}
								>
									<input
										type="checkbox"
										readOnly
										checked={filteredOptions.every((o) =>
											(selectedFilters[filterName] || []).includes(o),
										)}
										style={{ ...styles.checkbox, accentColor: "#cc0000" }}
									/>
									Select All ({filteredOptions.length})
								</div>
							)}{" "}
							{filteredOptions.length === 0 ? (
								<div
									style={{ padding: "8px", color: "#999", fontStyle: "italic" }}
								>
									No results
								</div>
							) : (
								<>
									{filteredOptions.map((option) => (
										<div
											key={option}
											style={styles.checkboxItem}
											onClick={() => handleFilterToggle(filterName, option)}
										>
											<input
												type="checkbox"
												checked={(selectedFilters[filterName] || []).includes(
													option,
												)}
												onChange={() => {}}
												style={styles.checkbox}
											/>
											<span>{option}</span>
										</div>
									))}
									{/* Footer: show count, and "X of Y filtered (Z total)" when restricted */}
									<div
										style={{
											padding: "6px 12px",
											borderTop: "1px solid #eee",
											backgroundColor: "#f9f9f9",
											fontSize: "11px",
											color: "#888",
											textAlign: "center",
											fontFamily: "MAN EUROPE_Regular",
										}}
									>
										{" "}
										{filterBadge && safeOptions.length !== totalOptions.length
											? `${filteredOptions.length} of ${safeOptions.length} filtered (${totalOptions.length} total)`
											: `${filteredOptions.length} option${filteredOptions.length !== 1 ? "s" : ""} available`}
									</div>
								</>
							)}
						</div>
					)}
				</div>
			</div>
		);
	};

	const renderFetRangePicker = () => {
		const fetFrom = selectedFilters.fet?.from || "";
		const fetTo = selectedFilters.fet?.to || "";
		const hasValue = fetFrom || fetTo;

		return (
			<div style={styles.filterGroup}>
				<label style={styles.filterLabel}>FET Range</label>
				<div
					style={{
						display: "flex",
						gap: "6px",
						alignItems: "center",
						width: "90%",
					}}
				>
					<div style={{ flex: 1 }}>
						<label
							style={{
								fontSize: "11px",
								color: "#666",
								display: "block",
								marginBottom: "2px",
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							From
						</label>
						<input
							type="text"
							placeholder="e.g. 201204"
							value={fetFrom}
							onChange={(e) =>
								setSelectedFilters((prev) => ({
									...prev,
									fet: { ...prev.fet, from: e.target.value },
								}))
							}
							style={{
								width: "100%",
								padding: "7px 6px",
								border: `1px solid ${fetFrom ? "#cc0000" : "#ddd"}`,
								borderRadius: "4px",
								fontSize: "13px",
								fontFamily: "MAN EUROPE_Regular",
								boxSizing: "border-box",
								outline: "none",
							}}
						/>
					</div>
					<div style={{ flex: 1 }}>
						<label
							style={{
								fontSize: "11px",
								color: "#666",
								display: "block",
								marginBottom: "2px",
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							To
						</label>
						<input
							type="text"
							placeholder="e.g. 201210"
							value={fetTo}
							onChange={(e) =>
								setSelectedFilters((prev) => ({
									...prev,
									fet: { ...prev.fet, to: e.target.value },
								}))
							}
							style={{
								width: "100%",
								padding: "7px 6px",
								border: `1px solid ${fetTo ? "#cc0000" : "#ddd"}`,
								borderRadius: "4px",
								fontSize: "13px",
								fontFamily: "MAN EUROPE_Regular",
								boxSizing: "border-box",
								outline: "none",
							}}
						/>
					</div>
				</div>
				{hasValue && (
					<button
						onClick={() =>
							setSelectedFilters((prev) => ({
								...prev,
								fet: { from: "", to: "" },
							}))
						}
						style={{
							marginTop: "4px",
							background: "none",
							border: "none",
							color: "#cc0000",
							fontSize: "11px",
							cursor: "pointer",
							fontFamily: "MAN EUROPE_Regular",
							padding: "0",
						}}
					>
						✕ Clear FET range
					</button>
				)}
			</div>
		);
	};

	const handleCollapsedSidebarClick = () => {
		if (isSidebarCollapsed) {
			setIsSidebarCollapsed(false);
		}
	};

	const styles = {
		container: {
			width: isSidebarCollapsed ? "50px" : "250px",
			backgroundColor: isSidebarCollapsed && isHovered ? "#e8e8e8" : "#f8f8f8",
			padding: 0,
			transition: "all 0.5s ease",
			position: "relative",
			overflow: "hidden",
			paddingBottom: "10px",
			borderRight: "2px solid black",
			borderTop: "2px solid black",
			height: "100%",
			fontFamily: "MAN EUROPE_Regular",
			cursor: isSidebarCollapsed ? "pointer" : "default",
			display: "flex",
			flexDirection: "column",
		},
		header: {
			display: "flex",
			alignItems: "center",
			padding: "15px",
			backgroundColor: isSidebarCollapsed && isHovered ? "#e8e8e8" : "#f8f8f8",
			justifyContent: isSidebarCollapsed ? "center" : "space-between",
			flexDirection: isSidebarCollapsed ? "column" : "row",
			transition: "background-color 0.3s ease",
			flexShrink: 0,
		},
		title: {
			fontSize: "16px",
			margin: 0,
			display: "flex",
			alignItems: "center",
			fontFamily: "MAN EUROPE_Bold",
		},
		toggleButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			transition: "background-color 0.2s ease",
		},
		content: {
			opacity: isSidebarCollapsed ? 0 : 1,
			visibility: isSidebarCollapsed ? "hidden" : "visible",
			transition: "opacity 0.2s ease",
			width: "100%",
			padding: "10px 4px 10px 10px",
			flex: 1,
			overflowY: hasScrollableContent ? "scroll" : "auto",
			overflowX: "hidden",
			maxHeight: "calc(100vh - 74px)",
			minHeight: "300px",
			scrollBehavior: "smooth",
			position: "relative",
			boxSizing: "border-box",
		},
		filterLabel: {
			display: "block",
			fontSize: "14px",
			marginBottom: "6px",
			color: "#333",
			fontFamily: "MAN EUROPE_Bold",
		},
		filterGroup: {
			marginBottom: "15px",
		},
		customDropdown: {
			position: "relative",
			width: "90%",
			marginBottom: "16px",
		},
		dropdownButton: {
			width: "100%",
			padding: "8px",
			border: "1px solid #ddd",
			backgroundColor: "white",
			cursor: "pointer",
			fontFamily: "MAN EUROPE_Regular",
			fontSize: "14px",
			boxSizing: "border-box",
			textAlign: "left",
		},
		dropdownContent: {
			position: "absolute",
			top: "100%",
			left: 0,
			right: 0,
			backgroundColor: "white",
			border: "1px solid #ddd",
			borderTop: "none",
			fontSize: "12px",
			zIndex: 1000,
			maxHeight: "200px",
			overflowY: "auto",
		},
		checkboxItem: {
			padding: "8px 12px",
			display: "flex",
			alignItems: "center",
			cursor: "pointer",
			fontFamily: "MAN EUROPE_Regular",
		},
		checkbox: {
			marginRight: "8px",
		},
		errorContainer: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			padding: "20px",
		},
		retryButton: {
			padding: "8px",
			backgroundColor: "#cc0000",
			border: "1px solid #cc0000",
			cursor: "pointer",
			color: "white",
			borderRadius: "4px",
			fontFamily: "MAN EUROPE_Bold",
			marginTop: "10px",
		},
		accordionContainer: {
			position: "relative",
			display: "flex",
			alignItems: "stretch",
		},
		accordionToggle: {
			position: "absolute",
			right: "-40px",
			top: "20px",
			width: "40px",
			height: "40px",
			backgroundColor: "#cc0000",
			border: "none",
			borderRadius: "0 8px 8px 0",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			color: "white",
			fontSize: "14px",
			fontWeight: "bold",
			zIndex: 1001,
			transition: "all 0.3s ease",
			boxShadow: "2px 2px 8px rgba(0,0,0,0.2)",
		},
		accordionPanel: {
			position: "absolute",
			left: "100%",
			top: 0,
			width: isAccordionOpen ? "300px" : "0px",
			height: "100%",
			backgroundColor: "#f8f8f8",
			borderRight: isAccordionOpen ? "2px solid black" : "none",
			borderTop: "2px solid black",
			borderBottom: "2px solid black",
			overflow: "hidden",
			transition: "width 0.3s ease",
			zIndex: 1000,
			boxShadow: isAccordionOpen ? "2px 0 8px rgba(0,0,0,0.1)" : "none",
		},
		accordionContent: {
			padding: isAccordionOpen ? "20px" : "0",
			opacity: isAccordionOpen ? 1 : 0,
			transition: "opacity 0.2s ease",
			height: "100%",
			overflowY: "auto",
			fontFamily: "MAN EUROPE_Regular",
		},
		accordionTitle: {
			fontSize: "18px",
			fontFamily: "MAN EUROPE_Bold",
			color: "#333",
			marginBottom: "20px",
			textAlign: "center",
			borderBottom: "2px solid #cc0000",
			paddingBottom: "10px",
		},
	};

	return (
		<div style={styles.accordionContainer}>
			{/* Main Sidebar */}
			<div
				className="sidebar-container filter-section"
				style={styles.container}
				onClick={handleCollapsedSidebarClick}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				title={isSidebarCollapsed ? "Click to expand filters" : ""}
			>
				<div style={styles.header}>
					{!isSidebarCollapsed && (
						<h2 style={styles.title}>
							<span
								style={{
									marginLeft: "8px",
									fontSize: "25px",
									fontFamily: "MAN EUROPE_Bold",
									fontWeight: "normal",
								}}
							>
								Filters
							</span>
						</h2>
					)}
					<button
						style={styles.toggleButton}
						onClick={(e) => {
							e.stopPropagation();
							setIsSidebarCollapsed(!isSidebarCollapsed);
						}}
						title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
					>
						{isSidebarCollapsed ? (
							<ChevronRight style={{ width: "20px", height: "20px" }} />
						) : (
							<ChevronLeft style={{ width: "20px", height: "20px" }} />
						)}
					</button>
				</div>

				<div
					ref={contentRef}
					className="ptsachnr-sidebar-content"
					style={styles.content}
					onClick={(e) => !isSidebarCollapsed && e.stopPropagation()}
				>
					{isLoading ? (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								padding: "20px",
							}}
						>
							<CircularProgress size={24} sx={{ color: "#cc0000" }} />
							<Typography
								variant="body2"
								sx={{ marginTop: "10px", color: "#666" }}
							>
								Loading filters...
							</Typography>
						</div>
					) : error ? (
						<div style={styles.errorContainer}>
							<Typography
								variant="body2"
								sx={{ color: "red", textAlign: "center" }}
							>
								{error}
							</Typography>
							<button style={styles.retryButton} onClick={retryLoadingFilters}>
								Retry
							</button>
						</div>
					) : (
						<>
							{" "}
							{renderCustomDropdown(
								"programm",
								getFilteredOptions("programms"),
								"Project Name",
							)}
							{renderCustomDropdown(
								"project",
								getFilteredOptions("projects"),
								"Project",
							)}{" "}
							{/* Purchase Type — grouped header with BY SET + BY SYSTEM sub-dropdowns */}
							<div style={styles.filterGroup}>
								<label style={styles.filterLabel}>Purchase Type</label>
								<div style={{ marginBottom: "-8px" }}>
									{renderCustomDropdown(
										"purchaseType",
										getFilteredOptions("purchaseTypes"),
										"BY SET",
									)}
								</div>
								<div>
									{renderCustomDropdown(
										"purchaseTypeSystem",
										getFilteredOptions("purchaseTypeSystems"),
										"BY SYSTEM",
									)}
								</div>
							</div>
							{renderCustomDropdown(
								"seTeams",
								getFilteredOptions("seTeams"),
								"SE-Teams",
							)}
							<div style={styles.filterGroup}>
								<label style={styles.filterLabel}>View type</label>
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: "8px",
										marginTop: "5px",
									}}
								>
									{["Prototype", "VFF", "Series"].map((type) => (
										<div
											key={type}
											style={{
												display: "flex",
												alignItems: "center",
												cursor: "pointer",
												padding: "4px 0",
												fontFamily: "MAN EUROPE_Regular",
												fontWeight: "normal",
												fontSize: "14px",
											}}
											onClick={() => onViewTypeChange(type)}
										>
											<input
												type="radio"
												id={`view-type-${type}`}
												name="viewType"
												checked={selectedViewType === type}
												onChange={() => onViewTypeChange(type)}
												style={{ marginRight: "8px", cursor: "pointer" }}
											/>
											<label
												htmlFor={`view-type-${type}`}
												style={{ cursor: "pointer", margin: 0, flex: 1 }}
											>
												{type}
											</label>
										</div>
									))}
								</div>
							</div>
							{renderCustomDropdown(
								"partNoPrefix",
								getFilteredOptions("partNoPrefix"),
								"Part No Prefix",
							)}
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "10px",
									width: "90%",
									marginTop: "20px",
								}}
							>
								<button
									onClick={toggleAccordion}
									data-tour-target="additional-filters-toggle"
									style={{
										padding: "8px",
										backgroundColor: isAccordionOpen ? "#cc0000" : "#545454",
										color: "white",
										border: "none",
										borderRadius: "0px",
										cursor: "pointer",
										fontFamily: "MAN EUROPE_Bold",
										fontSize: "14px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "8px",
										transition: "background-color 0.3s ease",
									}}
									title={
										isAccordionOpen
											? "Close additional filters"
											: "Open additional filters"
									}
								>
									Additional Filters
									<ChevronRight
										style={{
											width: "16px",
											height: "16px",
											transform: isAccordionOpen
												? "rotate(90deg)"
												: "rotate(0deg)",
											transition: "transform 0.3s ease",
										}}
									/>
								</button>

								<button
									onClick={applyFilters}
									style={{
										padding: "8px",
										backgroundColor: "#cc0000",
										border: "1px solid #cc0000",
										cursor: "pointer",
										color: "white",
										borderRadius: "0px",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									Apply Filters
								</button>

								<button
									onClick={clearFilters}
									style={{
										padding: "8px",
										backgroundColor: "#1e2832",
										border: "1px solid #ddd",
										cursor: "pointer",
										color: "#fff",
										borderRadius: "0px",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									Clear all Filters
								</button>
							</div>
						</>
					)}

					{/* Scroll indicators */}
					{!isSidebarCollapsed &&
						!isLoading &&
						!error &&
						hasScrollableContent && (
							<>
								{canScrollUp && (
									<div
										className="ptsachnr-sidebar-scroll-indicator-top"
										style={{
											position: "sticky",
											top: 0,
											left: 0,
											right: 0,
											height: "8px",
											pointerEvents: "none",
											zIndex: 10,
										}}
									/>
								)}
								{canScrollDown && (
									<div
										className="ptsachnr-sidebar-scroll-indicator-bottom"
										style={{
											position: "sticky",
											bottom: 0,
											left: 0,
											right: 0,
											height: "8px",
											pointerEvents: "none",
											zIndex: 10,
										}}
									/>
								)}
							</>
						)}
				</div>
			</div>

			{/* Accordion Panel */}
			<div
				style={styles.accordionPanel}
				className="accordion-panel additional-filters expandable-filters"
				data-tour-target="additional-filters-modal"
			>
				<div style={styles.accordionContent}>
					{isAccordionOpen && (
						<>
							<div
								style={{
									...styles.accordionTitle,
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<span>Additional Filters</span>
								<button
									style={{
										background: "none",
										border: "none",
										cursor: "pointer",
										padding: "4px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										borderRadius: "4px",
										transition: "background-color 0.2s ease",
									}}
									onClick={toggleAccordion}
									data-tour-target="additional-filters-close"
									title="Close additional filters"
									aria-label="Close additional filters"
								>
									<ChevronLeft style={{ width: "20px", height: "20px" }} />
								</button>
							</div>

							{renderCustomDropdown(
								"rampUpGroup",
								getFilteredOptions("rampUpGroups"),
								"ALG",
							)}
							{renderCustomDropdown(
								"flag",
								getFilteredOptions("flags"),
								"Flag",
							)}
							{renderCustomDropdown(
								"aenderungsnummer",
								getFilteredOptions("aenderungsnummers"),
								"Change No",
							)}
							{renderCustomDropdown(
								"changeDescription",
								getFilteredOptions("changeDescriptions"),
								"Change Description",
								"startsWith",
							)}
							{renderFetRangePicker()}
							{renderCustomDropdown(
								"dept",
								getFilteredOptions("depts"),
								"Dept",
							)}

							{/* Purchase Type Mismatch Filter */}
							<div style={styles.filterGroup}>
								<label style={styles.filterLabel}>Purchase Type Check</label>
								<label
									style={{
										display: "flex",
										alignItems: "flex-start",
										gap: "10px",
										padding: "10px 12px",
										backgroundColor: selectedFilters.showMismatchOnly
											? "#fff0f0"
											: "white",
										border: `1px solid ${selectedFilters.showMismatchOnly ? "#e40045" : "#ddd"}`,
										borderRadius: "4px",
										cursor: "pointer",
										transition: "all 0.2s ease",
										width: "90%",
										boxSizing: "border-box",
									}}
								>
									<input
										type="checkbox"
										checked={selectedFilters.showMismatchOnly}
										onChange={() =>
											setSelectedFilters((prev) => ({
												...prev,
												showMismatchOnly: !prev.showMismatchOnly,
											}))
										}
										style={{
											marginTop: "2px",
											accentColor: "#e40045",
											cursor: "pointer",
											flexShrink: 0,
										}}
									/>
									<div>
										<div
											style={{
												fontSize: "13px",
												fontFamily: "MAN EUROPE_Bold",
												color: selectedFilters.showMismatchOnly
													? "#e40045"
													: "#333",
												lineHeight: "1.3",
											}}
										>
											Show BY SET / BY SYSTEM mismatches only
										</div>
										<div
											style={{
												fontSize: "11px",
												color: "#888",
												fontFamily: "MAN EUROPE_Regular",
												marginTop: "3px",
												lineHeight: "1.4",
											}}
										>
											Rows where BY SET and BY SYSTEM differ
										</div>
									</div>
								</label>
							</div>

							{/* Hide Columns Temporarily */}
							<div style={styles.filterGroup}>
								<label style={styles.filterLabel}>
									Hide Columns Temporarily
								</label>
								<div style={styles.customDropdown} className="custom-dropdown">
									<div
										style={styles.dropdownButton}
										onClick={() => toggleDropdown("columns")}
									>
										<span
											style={{
												color: columnsToHide.length === 0 ? "#999" : "#333",
												display: "inline-block",
												width: "calc(100% - 20px)",
											}}
										>
											{getHideColumnsDropdownText()}
										</span>
										<span style={{ float: "right", marginTop: "2px" }}>
											<ChevronDown
												style={{ width: "16px", height: "16px", color: "#666" }}
											/>
										</span>
									</div>
									{openDropdowns.columns && (
										<div style={styles.dropdownContent}>
											{Object.entries({
												rampUpGroup: "Ramp-up group",
												seTeams: "SE-Teams",
												purchaseType: "Purchase Type",
												supplier: "Supplier",
											}).map(([key, label]) => (
												<div
													key={key}
													style={styles.checkboxItem}
													onClick={() => handleHideColumnCheckboxChange(key)}
												>
													<input
														type="checkbox"
														checked={columnsToHide.includes(key)}
														onChange={() => {}}
														style={styles.checkbox}
													/>
													<span>{label}</span>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default PTSachNrSidebar;
